import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getCheckoutStatus } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | success | timeout | error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate("/dashboard", { replace: true });
      return;
    }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      if (n >= 12) {
        setStatus("timeout");
        return;
      }
      try {
        const s = await getCheckoutStatus(sessionId);
        if (s.status === "complete" && (s.payment_status === "paid" || s.payment_status === "no_payment_required")) {
          await refresh();
          setStatus("success");
          setTimeout(() => navigate("/dashboard", { replace: true }), 1800);
          return;
        }
        if (s.status === "expired") {
          setStatus("error");
          return;
        }
      } catch (e) {
        // ignore and retry
      }
      setAttempts(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen grid place-items-center px-6 bg-background">
      <div className="text-center max-w-md" data-testid="billing-success-screen">
        {status === "checking" && (
          <>
            <div className="w-14 h-14 rounded-full bg-brand-lime mx-auto grid place-items-center mb-5">
              <Loader2 className="w-6 h-6 animate-spin text-brand-ink" />
            </div>
            <p className="font-display text-2xl font-black">Procesando tu suscripción…</p>
            <p className="text-sm text-foreground/65 mt-2">Stripe está confirmando todo. Intento {attempts + 1}/12.</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 rounded-full bg-brand-tea mx-auto grid place-items-center mb-5">
              <CheckCircle2 className="w-8 h-8 text-white" strokeWidth={2.5} />
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight">¡Listo! Bienvenido.</h1>
            <p className="text-foreground/65 mt-2">Tu prueba de 5 días empieza ahora. Te llevamos al panel…</p>
          </>
        )}
        {status === "timeout" && (
          <>
            <p className="font-display text-2xl font-black">Tarda más de lo normal</p>
            <p className="text-sm text-foreground/65 mt-2">Recarga la página en un momento o entra al panel.</p>
            <button onClick={() => navigate("/dashboard")} className="mt-5 btn-pill bg-brand-purple text-white">Ir al panel</button>
          </>
        )}
        {status === "error" && (
          <>
            <p className="font-display text-2xl font-black">El pago no se completó</p>
            <p className="text-sm text-foreground/65 mt-2">Vuelve a intentarlo cuando quieras.</p>
            <button onClick={() => navigate("/dashboard")} className="mt-5 btn-pill bg-brand-purple text-white">Volver</button>
          </>
        )}
      </div>
    </div>
  );
}
